/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type FC, useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import EditorDoneIcon from '@atlaskit/icon/core/check-mark';
import CopyIcon from '@atlaskit/icon/core/copy';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Tooltip from '@atlaskit/tooltip';

const RESET_DELAY = 2000;

enum CopyState {
	READY = 'READY',
	COPIED = 'COPIED',
	FAILED = 'FAILED',
}

interface CopyButtonProps {
	unitId: string;
}

export const CopyButton: FC<CopyButtonProps> = ({ unitId }) => {
	const { formatMessage } = useIntl();
	const [state, setState] = useState<CopyState>(CopyState.READY);

	const handleCopy = useCallback(async () => {
		if (!unitId) {
			return;
		}

		try {
			await navigator.clipboard.writeText(unitId);
			setState(CopyState.COPIED);
		} catch {
			setState(CopyState.FAILED);
		}

		setTimeout(() => setState(CopyState.READY), RESET_DELAY);
	}, [unitId]);

	const getIcon = () => {
		switch (state) {
			case CopyState.COPIED:
				return EditorDoneIcon;
			case CopyState.FAILED:
				return StatusErrorIcon;
			default:
				return CopyIcon;
		}
	};

	const getTooltipContent = () => {
		switch (state) {
			case CopyState.COPIED:
				return formatMessage({
					id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.unit-id.copySuccessTooltip',
					defaultMessage: 'Copied!',
				});
			case CopyState.FAILED:
				return formatMessage({
					id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.unit-id.copyFailedTooltip',
					defaultMessage: 'Copy failed',
				});
			default:
				return formatMessage({
					id: 'organization.audit.log.side-panel.tab-panels.details.unit-section.unit-id.copyTooltip',
					defaultMessage: 'Copy to clipboard',
				});
		}
	};

	const IconComponent = getIcon();

	return (
		<Tooltip content={getTooltipContent()}>
			<IconButton
				testId={`copy-unit-id-button-${state}`}
				icon={IconComponent}
				onClick={handleCopy}
				spacing="compact"
				isDisabled={!unitId}
				label={getTooltipContent()}
				appearance="subtle"
			/>
		</Tooltip>
	);
};
